import { computed, reactive } from 'vue'

export interface PlatformFont {
  id: string
  name: string
  family: string
  description: string
  sample: string
}

export interface CustomFont {
  id: string
  name: string
  family: string
  originalName: string
  mimeType: string
  sizeBytes: number
  createdAt: string
}

interface StoredFont extends CustomFont {
  data: ArrayBuffer
}

const DB_NAME = 'lumiscale-font-library'
const STORE_NAME = 'fonts'
const STORAGE_KEY = 'lumiscale-selected-font'
const HIDDEN_PLATFORM_FONTS_KEY = 'lumiscale-hidden-platform-fonts'
export const DEFAULT_FONT_ID = 'public:huosong'
const MAX_FONT_BYTES = 50 * 1024 * 1024
const MAX_LIBRARY_BYTES = 140 * 1024 * 1024
const MAX_CUSTOM_FONTS = 8

export const platformFonts: PlatformFont[] = [
  {
    id: 'public:qiji',
    name: '黄令东齐伋复刻体',
    family: 'Lumi Qiji',
    description: '个性化选择，古籍雕版感强，适合复古与叙事界面',
    sample: '心有微光，自成一域',
  },
  {
    id: 'public:fangsong',
    name: '寒蝉活仿宋',
    family: 'Lumi Chill FangSong',
    description: '个性化选择，清秀舒展，兼顾正式感与长文阅读',
    sample: '每一次回答，都值得被认真看见',
  },
  {
    id: 'public:huosong',
    name: '寒蝉活宋体',
    family: 'Lumi Chill Song',
    description: '网站默认与首发字体，温润端正，适合正文与结果报告',
    sample: '愿你在文字里听见自己的回响',
  },
]

const savedSelection = localStorage.getItem(STORAGE_KEY) || DEFAULT_FONT_ID

function savedHiddenPlatformFontIds() {
  try {
    const value = JSON.parse(localStorage.getItem(HIDDEN_PLATFORM_FONTS_KEY) || '[]')
    return Array.isArray(value) ? value.filter((id): id is string => typeof id === 'string' && platformFonts.some((font) => font.id === id)) : []
  } catch {
    return []
  }
}

export const fontLibrary = reactive({
  panelOpen: false,
  ready: false,
  uploading: false,
  dragActive: false,
  selectedId: savedSelection,
  hiddenPlatformIds: savedHiddenPlatformFontIds(),
  customFonts: [] as CustomFont[],
  message: '',
  error: '',
  limits: {
    maxFonts: MAX_CUSTOM_FONTS,
    maxFontBytes: MAX_FONT_BYTES,
    maxLibraryBytes: MAX_LIBRARY_BYTES,
  },
})

export const visiblePlatformFonts = computed(() => {
  return platformFonts.filter((item) => !fontLibrary.hiddenPlatformIds.includes(item.id))
})

export const selectedFontName = computed(() => {
  return platformFonts.find((item) => item.id === fontLibrary.selectedId)?.name
    || fontLibrary.customFonts.find((item) => item.id === fontLibrary.selectedId)?.name
    || '寒蝉活宋体'
})

const registeredFaces = new Map<string, FontFace>()
let initialization: Promise<void> | null = null

function platformFont(id: string) {
  return platformFonts.find((item) => item.id === id)
}

function visiblePlatformFont(id: string) {
  return visiblePlatformFonts.value.find((item) => item.id === id)
}

function fallbackPlatformFont() {
  return visiblePlatformFont(DEFAULT_FONT_ID) || visiblePlatformFonts.value[0] || platformFonts[2]
}

function saveHiddenPlatformFontIds() {
  localStorage.setItem(HIDDEN_PLATFORM_FONTS_KEY, JSON.stringify(fontLibrary.hiddenPlatformIds))
}

function applyFamily(id: string, family: string) {
  const fallback = '"Microsoft YaHei", "PingFang SC", "Noto Serif SC", system-ui, sans-serif'
  document.documentElement.style.setProperty('--font-active', `"${family}", ${fallback}`)
  document.documentElement.dataset.fontSelection = id
  fontLibrary.selectedId = id
  localStorage.setItem(STORAGE_KEY, id)
}

function applyInitialFont() {
  const selected = visiblePlatformFont(savedSelection) || fallbackPlatformFont()
  applyFamily(selected.id, selected.family)
}

applyInitialFont()

function openDatabase() {
  return new Promise<IDBDatabase>((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, 1)
    request.onupgradeneeded = () => {
      if (!request.result.objectStoreNames.contains(STORE_NAME)) {
        request.result.createObjectStore(STORE_NAME, { keyPath: 'id' })
      }
    }
    request.onsuccess = () => resolve(request.result)
    request.onerror = () => reject(request.error)
  })
}

async function getStoredFonts() {
  const database = await openDatabase()
  return new Promise<StoredFont[]>((resolve, reject) => {
    const request = database.transaction(STORE_NAME, 'readonly').objectStore(STORE_NAME).getAll()
    request.onsuccess = () => resolve(request.result as StoredFont[])
    request.onerror = () => reject(request.error)
  })
}

async function storeFont(record: StoredFont) {
  const database = await openDatabase()
  return new Promise<void>((resolve, reject) => {
    const transaction = database.transaction(STORE_NAME, 'readwrite')
    transaction.objectStore(STORE_NAME).put(record)
    transaction.oncomplete = () => resolve()
    transaction.onerror = () => reject(transaction.error)
  })
}

async function deleteStoredFont(id: string) {
  const database = await openDatabase()
  return new Promise<void>((resolve, reject) => {
    const transaction = database.transaction(STORE_NAME, 'readwrite')
    transaction.objectStore(STORE_NAME).delete(id)
    transaction.oncomplete = () => resolve()
    transaction.onerror = () => reject(transaction.error)
  })
}

async function registerFont(record: StoredFont) {
  const face = new FontFace(record.family, record.data)
  await face.load()
  document.fonts.add(face)
  registeredFaces.set(record.id, face)
}

export function initializeFontLibrary() {
  if (initialization) return initialization
  initialization = (async () => {
    try {
      const records = await getStoredFonts()
      for (const record of records) {
        try {
          await registerFont(record)
          const { data: _data, ...metadata } = record
          fontLibrary.customFonts.push(metadata)
        } catch {
          // Keep a damaged local font from blocking the rest of the library.
        }
      }
      const selectedCustom = records.find((item) => item.id === savedSelection && registeredFaces.has(item.id))
      if (selectedCustom) applyFamily(selectedCustom.id, selectedCustom.family)
      else if (!visiblePlatformFont(fontLibrary.selectedId)) {
        const fallback = fallbackPlatformFont()
        applyFamily(fallback.id, fallback.family)
      }
    } catch {
      fontLibrary.error = '浏览器字体库暂时无法读取，仍可使用三套平台字体。'
    } finally {
      fontLibrary.ready = true
    }
  })()
  return initialization
}

export async function selectFont(id: string) {
  fontLibrary.error = ''
  fontLibrary.message = ''
  const preset = visiblePlatformFont(id)
  if (preset) {
    applyFamily(preset.id, preset.family)
    fontLibrary.message = `已切换为${preset.name}。`
    void document.fonts.load(`16px "${preset.family}"`)
    return
  }
  const custom = fontLibrary.customFonts.find((item) => item.id === id)
  if (!custom || !registeredFaces.has(id)) {
    fontLibrary.error = '这套字体没有成功加载，请重新拖入字体文件。'
    return
  }
  applyFamily(custom.id, custom.family)
  fontLibrary.message = `已切换为${custom.name}。`
}

function validFontFile(file: File) {
  return /\.(ttf|otf|woff2?)$/i.test(file.name)
}

function fontMime(file: File) {
  const extension = file.name.toLowerCase().split('.').pop()
  if (extension === 'ttf') return 'font/ttf'
  if (extension === 'otf') return 'font/otf'
  if (extension === 'woff') return 'font/woff'
  if (extension === 'woff2') return 'font/woff2'
  return file.type || 'application/octet-stream'
}

function displayName(fileName: string) {
  return fileName.replace(/\.(ttf|otf|woff2?)$/i, '').replace(/[_-]+/g, ' ').trim().slice(0, 48) || '我的字体'
}

export async function uploadFonts(files: File[]) {
  fontLibrary.error = ''
  fontLibrary.message = ''
  const candidates = files.filter(validFontFile)
  if (!candidates.length) {
    fontLibrary.error = '请选择 TTF、OTF、WOFF 或 WOFF2 字体文件。'
    return
  }
  if (fontLibrary.customFonts.length + candidates.length > MAX_CUSTOM_FONTS) {
    fontLibrary.error = `最多保存 ${MAX_CUSTOM_FONTS} 套自定义字体，请先删除不再使用的字体。`
    return
  }
  const currentBytes = fontLibrary.customFonts.reduce((sum, item) => sum + item.sizeBytes, 0)
  const incomingBytes = candidates.reduce((sum, item) => sum + item.size, 0)
  if (candidates.some((file) => file.size > MAX_FONT_BYTES)) {
    fontLibrary.error = '单个字体不能超过 50 MB。'
    return
  }
  if (currentBytes + incomingBytes > MAX_LIBRARY_BYTES) {
    fontLibrary.error = '字体库总容量不能超过 140 MB，请先删除不再使用的字体。'
    return
  }

  fontLibrary.uploading = true
  let lastAdded: CustomFont | null = null
  try {
    for (const file of candidates) {
      const id = `custom:${crypto.randomUUID()}`
      const family = `Lumi User ${id.slice(-12)}`
      const data = await file.arrayBuffer()
      const record: StoredFont = {
        id,
        name: displayName(file.name),
        family,
        originalName: file.name,
        mimeType: fontMime(file),
        sizeBytes: file.size,
        createdAt: new Date().toISOString(),
        data,
      }
      await registerFont(record)
      await storeFont(record)
      const { data: _data, ...metadata } = record
      fontLibrary.customFonts.unshift(metadata)
      lastAdded = metadata
    }
    if (lastAdded) {
      await selectFont(lastAdded.id)
      fontLibrary.message = `${candidates.length} 套字体已存入当前浏览器，并已应用到全站。`
    }
  } catch (error) {
    fontLibrary.error = error instanceof DOMException && error.name === 'QuotaExceededError'
      ? '浏览器可用空间不足，请删除部分自定义字体后再试。'
      : '字体无法识别或保存，请确认文件没有损坏。'
  } finally {
    fontLibrary.uploading = false
    fontLibrary.dragActive = false
  }
}

export async function removeCustomFont(id: string) {
  const item = fontLibrary.customFonts.find((font) => font.id === id)
  if (!item) return
  fontLibrary.error = ''
  try {
    await deleteStoredFont(id)
    const face = registeredFaces.get(id)
    if (face) document.fonts.delete(face)
    registeredFaces.delete(id)
    fontLibrary.customFonts = fontLibrary.customFonts.filter((font) => font.id !== id)
    if (fontLibrary.selectedId === id) await selectFont(DEFAULT_FONT_ID)
    fontLibrary.message = `已删除${item.name}。`
  } catch {
    fontLibrary.error = '字体删除失败，请刷新页面后重试。'
  }
}

export function removePlatformFont(id: string) {
  const item = platformFont(id)
  if (!item || fontLibrary.hiddenPlatformIds.includes(id)) return
  if (visiblePlatformFonts.value.length <= 1) {
    fontLibrary.error = '请至少保留一套平台字体，避免全站没有可用的内嵌字体。'
    return
  }

  fontLibrary.error = ''
  const fallback = visiblePlatformFonts.value.find((font) => font.id !== id) || fallbackPlatformFont()
  fontLibrary.hiddenPlatformIds.push(id)
  saveHiddenPlatformFontIds()
  if (fontLibrary.selectedId === id) applyFamily(fallback.id, fallback.family)
  fontLibrary.message = `已从当前浏览器的字体衣橱中移除${item.name}。`
}

export function restorePlatformFonts() {
  if (!fontLibrary.hiddenPlatformIds.length) return
  fontLibrary.error = ''
  fontLibrary.hiddenPlatformIds.splice(0)
  saveHiddenPlatformFontIds()
  fontLibrary.message = '已恢复全部平台字体。'
}

export function openFontLibrary() {
  fontLibrary.panelOpen = true
  fontLibrary.message = ''
  fontLibrary.error = ''
  void initializeFontLibrary()
}

export function closeFontLibrary() {
  fontLibrary.panelOpen = false
  fontLibrary.dragActive = false
}
